import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, inject } from '@angular/core'
import { ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatRadioChange, MatRadioModule } from '@angular/material/radio'
import { Store, select } from '@ngrx/store'
import { AccountObject, LocationObject } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { compact, uniq } from 'lodash-es'
import { Observable, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { loadCountries, selectCountriesByCode } from 'src/app/store/countries'
import { loadLocations, selectAllLocations, selectLocationEntities } from 'src/app/store/locations'
import { loadUnlocodes, selectAllUnlocodes } from 'src/app/store/unlocodes'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'
import { ReactiveAsteriskModule } from 'src/components/reactive-asterisk/reactive-asterisk.module'
import { SelectSearchComponent } from 'src/components/select-search/select-search.component'
import { SelectSearchModule } from 'src/components/select-search/select-search.module'
import { environment } from 'src/environments/environment'
import { isAtLeastAdmin } from 'src/services/data/users.service'
import { compareBy } from 'src/services/table-utils/compare'
import { disableIf } from 'src/shared/utils/disable-if'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { CompanyBulkFormGroup, CompanyFormGroup } from '../company-form'
import { CompanyLocationFormModule } from '../sub-forms/company-location-form/company-location-form.module'
import { CompanyLocationFormService } from '../sub-forms/company-location-form/company-location-form.service'


@Component({
    selector: 'tc-company-locations-tab',
    templateUrl: './company-locations-tab.component.html',
    styleUrl: './company-locations-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatInputModule,
        MatRadioModule,
        ReactiveFormsModule,
        CompanyLocationFormModule,
        ReactiveAsteriskModule,
        SelectSearchModule,
    ]
})
export class CompanyLocationsTabComponent extends OnDestroyMixin implements OnInit {
  private readonly AuthApi = inject(AuthApiService)
  private readonly store = inject(Store)
  private readonly CompanyLocationForm = inject(CompanyLocationFormService)
  private readonly ConfirmModal = inject(ConfirmModalService)

  constructor() {
    super()
    this.enableRouteService = environment.enableRouteService ?? false;
    this.enableFreightRatePaths = environment.enableFreightRatePaths ?? false;
  }

  enableRouteService = false;
  enableFreightRatePaths = false;

  protected readonly isAdminManagerSuperuser = isAtLeastAdmin(this.AuthApi.currentUser)

  protected readonly countries$ = this.store.pipe(select(selectCountriesByCode), waitNotEmpty())
  protected readonly locations$ = this.store.pipe(select(selectAllLocations), waitNotEmpty())
  protected readonly unlocodes$ = this.store.pipe(select(selectAllUnlocodes), waitNotEmpty())

  @Input({ required: true }) companyForm: CompanyFormGroup
  @Input() bulkForm: CompanyBulkFormGroup
  @Input() isBulkEdit: boolean
  @Input() selectedCompanies$: Observable<DeepReadonly<AccountObject[]>>
  @ViewChild('prefPort') preferredPortElement: SelectSearchComponent<string>;

  protected companyLocations$: Observable<DeepReadonly<LocationObject>[]>
  protected deletableLocations$: Observable<DeepReadonly<LocationObject>[]>

  ngOnInit() {
    this.store.dispatch(loadLocations({}))
    this.store.dispatch(loadCountries())
    this.store.dispatch(loadUnlocodes());

    this.companyLocations$ = combineLatest([
      replayForm(this.companyForm.controls.locations),
      this.store.pipe(select(selectLocationEntities), waitNotEmpty()),
    ]).pipe(map(([companyLocations, locations]) =>
      // NOTE: data integrity check
      companyLocations?.map(loc => locations[loc] || {} as LocationObject) || []))

    if (!this.isBulkEdit) return

    this.deletableLocations$ = combineLatest([
      this.selectedCompanies$,
      this.store.pipe(select(selectLocationEntities), waitNotEmpty()),
    ]).pipe(map(([companies, locations]) =>
      compact(uniq(companies.flatMap(company => company.attributes?.locations || []))
        .map(loc => locations[loc]))
        .sort(compareBy('name'))))

    replayForm(this.bulkForm.controls.canEditLocations).pipe(untilComponentDestroyed(this)).subscribe(canEditLocations => {
      disableIf(this.bulkForm.controls.locationsToAdd, !canEditLocations)
      disableIf(this.bulkForm.controls.locationsToDelete, !canEditLocations)
      disableIf(this.bulkForm.controls.locationsToReplace, !canEditLocations)
    })
  }

  protected onDefaultLocationIsPortChange(change: MatRadioChange) {
    const ctrl = this.companyForm.controls.default_port;
    if (change.value) {
      ctrl.addValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity();
    this.preferredPortElement.checkIfRequired();
  }

  protected setDefaultLocation(locationId: string) {
    const ctrl = this.companyForm.controls.default_location
    ctrl.setValue(locationId)
    ctrl.markAsTouched()
    ctrl.markAsDirty()
  }

  protected async showAddLocation() {
    const loc = await this.CompanyLocationForm.showCompanyLocation()
    if (!loc) return
    const ctrl = this.companyForm.controls.locations
    ctrl.setValue([...ctrl.value, loc])
    ctrl.markAsTouched()
    ctrl.markAsDirty()
  }

  protected async showRemoveLocation(index: number) {
    const ctrl = this.companyForm.controls.locations
    const locId = ctrl.value[index]
    const taxCtrl = this.companyForm.controls.pricing.controls.tax_location
    const isDefault = locId === this.companyForm.controls.default_location.value
    const isTaxLocation = locId === taxCtrl.value

    // WA-20527: never orphan default_location — block removing the current
    // default and make the user reassign one first (per Kia, ticket comment).
    if (isDefault) {
      await this.ConfirmModal.show({
        title: 'Cannot remove default location',
        description: 'This location is set as the default. Set another location as the default before removing it.',
        confirmButtonText: 'OK',
        cancelButtonText: '', // info-only: hides the Cancel button (see tcConfirmModal)
      })
      return
    }

    // WA-20527: removing the Tax/Pricing (Incoterm) location must clear that
    // field too, otherwise it keeps pointing at a deleted location.
    await this.ConfirmModal.show(isTaxLocation ? {
      title: 'Remove this location?',
      description: 'This location is used as the Tax/Pricing (Incoterm) Location. Removing it will also clear that field.',
      confirmButtonText: 'Remove',
    } : {
      title: 'Remove this location?',
      description: 'Are you sure you want to remove this location?',
    })

    ctrl.setValue(ctrl.value.filter((_l, i) => i !== index))
    ctrl.markAsTouched()
    ctrl.markAsDirty()

    if (isTaxLocation) {
      taxCtrl.setValue(null)
      taxCtrl.markAsTouched()
      taxCtrl.markAsDirty()
    }
  }
}
